import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import Icon from '@amzn/awsui-components-console/icon';
import Tooltip from '@amzn/awsui-components-console/tooltip';

interface InfoTooltipProps {
  /**
   * Explanatory text shown on hover or focus. In icon mode it is also the
   * trigger's `aria-label`; in wrapper mode it is wired to the child via
   * `aria-describedby` — Cloudscape's Tooltip content is not exposed to AT.
   */
  readonly content: string;
  /** Placement relative to the trigger. Defaults to 'top'. */
  readonly position?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Optional trigger to wrap (e.g. a Button). When omitted, the shared info
   * icon is rendered (icon mode). When provided (wrapper mode), the tooltip
   * anchors to the wrapped trigger instead of an icon.
   */
  readonly children?: React.ReactNode;
  /**
   * Wrapper mode only: suppresses the tooltip, used when the wrapped trigger is
   * a button carrying its own `disabledReason`, so the two never both show.
   */
  readonly disabled?: boolean;
}

/**
 * Shared tooltip revealing text on hover or keyboard focus, per Cloudscape's
 * documented Tooltip contract: the trigger owns visibility and dismisses on
 * pointer leave, blur, or Escape, and it uses pointer events so touch/pen work.
 *
 * Two modes:
 * - Icon mode (no children): renders a focusable info icon whose accessible
 *   name IS the content. Any focus or hover reveals the tip.
 * - Wrapper mode (children): wraps a trigger (e.g. a Button). Reveals only on
 *   hover or genuine keyboard focus (:focus-visible) — a mouse click, and the
 *   focus restored when a dialog the trigger opened closes, do NOT re-show it.
 *   The content reaches AT via `aria-describedby` on the focusable child.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  position = 'top',
  children,
  disabled = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const descriptionId = useId();
  const isWrapper = children !== undefined;

  const getTrack = useCallback(() => triggerRef.current, []);
  const show = useCallback(() => {
    if (!disabled) setVisible(true);
  }, [disabled]);
  const hide = useCallback(() => setVisible(false), []);

  // Reset when a wrapped trigger becomes disabled, so a previously-hovered tip
  // cannot reappear via the `visible && !disabled` guard once it re-enables.
  useEffect(() => {
    if (disabled) setVisible(false);
  }, [disabled]);

  const showOnFocus = useCallback(
    (event: React.FocusEvent) => {
      if (disabled) return;
      // Icon mode: any focus reveals. Wrapper mode: only keyboard focus
      // (:focus-visible), degrading to hover-only where it is unsupported.
      if (!isWrapper) {
        setVisible(true);
        return;
      }
      const el = event.target as HTMLElement;
      let keyboardFocus = false;
      try {
        keyboardFocus = el.matches(':focus-visible');
      } catch {
        /* istanbul ignore next -- defensive: :focus-visible is widely supported; this guards only ancient engines where matches() throws */
        keyboardFocus = false;
      }
      if (keyboardFocus) setVisible(true);
    },
    [disabled, isWrapper],
  );

  if (!isWrapper) {
    return (
      <span
        ref={triggerRef}
        tabIndex={0}
        role="img"
        aria-label={content}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={showOnFocus}
        onBlur={hide}
      >
        {/* `size="inherit"` rather than a fixed size: Cloudscape sizes the icon from
            the inherited line height and centres it, so the icon suits its context
            instead of every call site being the same 16px. */}
        <Icon name="status-info" size="inherit" variant="link" />
        {visible && (
          <Tooltip
            content={content}
            getTrack={getTrack}
            position={position}
            onEscape={hide}
          />
        )}
      </span>
    );
  }

  // Wrapper mode: expose the help text to AT via aria-describedby on the ACTUAL
  // focusable child (screen readers announce describedby for the focused
  // element, not a wrapper span). Cloudscape's Button forwards `ariaDescribedby`.
  const describedChild =
    !disabled && React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement<{ ariaDescribedby?: string }>,
          { ariaDescribedby: descriptionId },
        )
      : children;

  return (
    <span
      ref={triggerRef}
      onPointerEnter={show}
      onPointerLeave={hide}
      onPointerDown={hide}
      onFocus={showOnFocus}
      onBlur={hide}
    >
      {describedChild}
      {!disabled && (
        <span id={descriptionId} hidden>
          {content}
        </span>
      )}
      {visible && !disabled && (
        <Tooltip
          content={content}
          getTrack={getTrack}
          position={position}
          onEscape={hide}
        />
      )}
    </span>
  );
};

export default InfoTooltip;
